import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    TemplateRef,
    Type,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FilterMetadata } from 'primeng/api';
import { AutoComplete } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { DatePicker } from 'primeng/datepicker';
import {
    DialogService,
    DynamicDialogModule,
    DynamicDialogRef
} from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelect } from 'primeng/multiselect';
import { PaginatorModule } from 'primeng/paginator';
import { Select } from 'primeng/select';
import { SelectButton } from 'primeng/selectbutton';
import {
    ColumnFilter,
    Table,
    TableModule
} from 'primeng/table';
import { Tag } from 'primeng/tag';
import { Tooltip } from 'primeng/tooltip';
import {
    Observable,
    Subscription
} from 'rxjs';

import { AppRoleComponent } from '@/components/app.role';
import { AppStatusComponent } from '@/components/app.status';
import { AvatarComponent } from '@/components/avatar/avatar.component';
import {
    DataTableColumnType,
    FilterType
} from '@/enums';
import { TRI_STATE_BOOLEAN_FILTER_OPTIONS } from '@/helpers';
import {
    DataTableAction,
    DataTableColumn,
    DataTableExportParams,
    DataTableParams
} from '@/interfaces';
import {
    JoinPipe,
    PhonePipe,
    ValueOrPipe
} from '@/pipes';

@Component({
    selector: 'app-data-table',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        TableModule,
        InputTextModule,
        ButtonModule,
        IconFieldModule,
        InputIconModule,
        PaginatorModule,
        Tag,
        AvatarComponent,
        PhonePipe,
        JoinPipe,
        DynamicDialogModule,
        ValueOrPipe,
        AppRoleComponent,
        AppStatusComponent,
        Select,
        MultiSelect,
        FormsModule,
        Tooltip,
        Checkbox,
        AutoComplete,
        DatePicker,
        SelectButton
    ],
    providers: [DialogService],
    templateUrl: 'data-table.component.html',
    styleUrls: ['./data-table.component.scss'],
})
export class DataTableComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

    @ViewChild('dataTable') dataTable!: Table;
    @ViewChildren(ColumnFilter) columnFilters!: QueryList<ColumnFilter>;

    @Input() title = '';
    @Input() columns: DataTableColumn[] = [];
    @Input() enableGlobalSearch = false;
    @Input() globalFilterFields: string[] = [];
    @Input() paginator = true;
    @Input() createBtnLabel: string | undefined = undefined;
    @Input() globalSearchPlaceholder = 'Search...';
    @Input() defaultSortField: string | null = null;
    @Input() defaultSortOrder: 'asc' | 'desc' = 'asc';
    @Input() fetchDataFn!: (params: DataTableParams) => Observable<{ data: any[]; total: number }>;
    @Input() exportDataFn!: (params: DataTableExportParams) => Observable<Blob>;
    @Input() reloadEmitter?: Observable<void>;
    @Input() exportable = false;
    @Input() loading = false;
    @Input() filterOptionsData?: any;
    @Input() toolbarExtraTemplate: TemplateRef<unknown> | null = null;
    /** Keep table header visible while scrolling table rows. */
    @Input() fixedHeader = true;
    /** Override viewport-based height, e.g. calc(100vh - 36rem) when content sits above the table. */
    @Input() scrollHeight: string | null = null;
    /** Used when fixedHeader is true and scrollHeight is not set. */
    @Input() scrollHeightOffsetRem = 20;

    @Output() filterChange = new EventEmitter<string>();
    @Output() onCreate = new EventEmitter<void>();

    ref?: DynamicDialogRef;
    data: any[] = [];
    totalRecords = 0;
    rowsPerPage = 25;
    search = '';

    subs = new Subscription();
    reloadSub?: Subscription;
    optionsLoading = new Set<string>();
    exportLoading = false;
    initialLoad = true;
    /** Skip the first unfiltered lazy load when route/query defaults will be applied in ngAfterViewInit. */
    private suppressInitialLoadWithoutDefaults = false;
    /** Detect filter/search/sort changes and reset paginator to the first page. */
    private lastQuerySnapshot = '';
    /** Last filters sent to the API — autocomplete/options must not use in-progress overlay values. */
    private lastAppliedFilterParams: Record<string, any> = {};
    private lastAppliedSearch = '';
    editingCell: { row: any; field: string; value: any; } | null = null;

    get resolvedScrollHeight(): string | undefined {
        if (this.scrollHeight) {
            return this.scrollHeight;
        }

        if (!this.fixedHeader) {
            return undefined;
        }

        return `calc(100vh - ${this.scrollHeightOffsetRem}rem)`;
    }

    constructor(
        private dialogService: DialogService,
        private cdr: ChangeDetectorRef
    ) {
    }

    get visibleColumns(): DataTableColumn[] {
        return this.columns.filter(col => {
            if (col.tableVisible === false) {
                return false;
            }

            return col.showIf ? col.showIf() : true;
        });
    }

    get toolbarFilterColumns(): DataTableColumn[] {
        return this.columns.filter(
            (col) => col.filterInToolbar && col.filterable !== false && (col.showIf ? col.showIf() : true)
        );
    }

    ngOnInit(): void {
        this.suppressInitialLoadWithoutDefaults = this.hasColumnDefaultFilters();

        if (this.reloadEmitter) {
            this.reloadSub = this.reloadEmitter.subscribe(() => {
                this.loadDataWithTableFilters(0);
            });
        }
    }

    ngAfterViewInit(): void {
        if (this.hasColumnDefaultFilters()) {
            this.loadDataWithTableFilters(0);
        }
    }

    ngOnChanges(): void {
        if (!this.filterOptionsData) {
            return;
        }

        this.columns.forEach(col => {
            if (!col.optionsKey || col.filterOptions?.length) {
                return;
            }

            const values = this.filterOptionsData[col.optionsKey];

            if (!Array.isArray(values)) {
                return;
            }

            col.filterOptions = values.map(v => ({
                label: col.optionLabelMap ? col.optionLabelMap(v) : String(v),
                value: col.optionValueMap ? col.optionValueMap(v) : v
            }));
        });

        this.cdr.markForCheck();
    }

    ngOnDestroy(): void {
        this.subs.unsubscribe();
        this.reloadSub?.unsubscribe();
        if (this.ref) {
            this.ref.close();
        }
    }

    /** Sync PrimeNG table filters from column `filterDefaultValue` (e.g. route query params). */
    applyColumnDefaultFilters(): boolean {
        if (!this.dataTable) {
            return false;
        }

        const filters: { [key: string]: FilterMetadata | FilterMetadata[] } = {
            ...this.dataTable.filters
        };
        let applied = false;

        for (const col of this.columns) {
            if (col.filterDefaultValue === undefined) {
                continue;
            }

            if (col.filterDefaultValue === null) {
                if (filters[col.field]) {
                    filters[col.field] = this.clearedFilterConstraint(col, filters[col.field]);
                    applied = true;
                }
                continue;
            }

            filters[col.field] = [{
                value: col.filterDefaultValue,
                matchMode: col.matchMode ?? this.defaultFilterMatchMode(col)
            }];
            applied = true;
        }

        if (applied) {
            this.dataTable.filters = filters;
        }

        return applied;
    }

    /** PrimeNG menu filters need a constraint row; deleting the field breaks the filter overlay UI. */
    private clearedFilterConstraint(
        col: DataTableColumn,
        existing: FilterMetadata | FilterMetadata[]
    ): FilterMetadata[] {
        const prev = Array.isArray(existing) ? existing[0] : undefined;
        return [{
            value: null,
            matchMode: col.matchMode ?? prev?.matchMode ?? this.defaultFilterMatchMode(col),
            operator: prev?.operator ?? 'and'
        }];
    }

    filterMatchMode(column: DataTableColumn): string {
        return column.matchMode ?? this.defaultFilterMatchMode(column);
    }

    private defaultFilterMatchMode(col: DataTableColumn): string {
        switch (col.filterType) {
            case FilterType.AUTOCOMPLETE:
            case FilterType.MULTISELECT:
                return 'in';
            case FilterType.SELECT:
            case FilterType.BOOLEAN:
                return 'equals';
            case FilterType.NUMERIC:
            case FilterType.DATE:
                return 'between';
            default:
                return 'contains';
        }
    }

    private hasColumnDefaultFilters(): boolean {
        return this.columns.some(
            (col) => col.filterDefaultValue !== undefined && col.filterDefaultValue !== null
        );
    }

    private loadDataWithTableFilters(first = 0): void {
        if (first === 0) {
            this.resetPaginator();
        }
        this.applyColumnDefaultFilters();
        const event = this.dataTable?.createLazyLoadMetadata?.() ?? {};
        this.loadData({
            ...event,
            first,
            filters: this.dataTable?.filters ?? event['filters']
        });
    }

    clearFilters(): void {
        this.search = '';
        this.resetPaginator();
        this.dataTable.clear();
    }

    private resetPaginator(): void {
        if (!this.dataTable) {
            return;
        }

        this.dataTable.first = 0;
        this.cdr.markForCheck();
    }

    private buildQuerySnapshot(
        filters: Record<string, any>,
        sort: { sort_field?: string; sort_order?: 'asc' | 'desc' }
    ): string {
        return JSON.stringify({
            filters,
            search: this.search,
            sort_field: sort.sort_field ?? null,
            sort_order: sort.sort_order ?? null,
        });
    }

    loadData(event: Record<string, any> = {}): void {
        document.querySelectorAll('.p-datatable-filter-overlay').forEach(el => el.remove());
        const rows = event['rows'] ?? this.rowsPerPage;
        const filters = this.normalizeFilters(event);

        if (
            this.suppressInitialLoadWithoutDefaults &&
            this.initialLoad &&
            !Object.keys(filters).length
        ) {
            return;
        }

        this.suppressInitialLoadWithoutDefaults = false;
        const sort = this.resolveSort(event);
        const snapshot = this.buildQuerySnapshot(filters, sort);

        if (this.lastQuerySnapshot && snapshot !== this.lastQuerySnapshot) {
            event['first'] = 0;
            this.resetPaginator();
        }

        const first = event['first'] ?? 0;
        this.lastQuerySnapshot = snapshot;
        this.lastAppliedFilterParams = filters;
        this.lastAppliedSearch = this.search;
        const params: any = {
            page: first / rows + 1,
            per_page: rows,
            ...filters,
            ...sort,
        };
        if (this.search) {
            params.search = this.search;
        }
        this.loading = true;
        this.cdr.markForCheck();
        const sub = this.fetchDataFn(params).subscribe({
            next: res => {
                this.data = [...res.data];
                this.totalRecords = res.total;
                this.loading = false;
                this.initialLoad = false;
                window.scrollTo({ top: 0, behavior: 'auto' });
                this.closeAllColumnFilters();
                this.cdr.markForCheck();
            },
            error: () => {
                this.loading = false;
                this.initialLoad = false;
                this.cdr.markForCheck();
            }
        });
        this.subs.add(sub);
    }

    onGlobalSearch(event: Event): void {
        this.search = (event.target as HTMLInputElement).value;
        this.resetPaginator();
        this.loadData({ first: 0, rows: this.rowsPerPage });
    }

    getToolbarFilterValue(column: DataTableColumn): boolean | null {
        const filterArr = this.dataTable?.filters?.[column.field];
        if (!Array.isArray(filterArr) || !filterArr.length) {
            return null;
        }

        const value = filterArr[0].value;
        return value === null || value === undefined ? null : value;
    }

    onToolbarFilterChange(column: DataTableColumn, value: boolean | null): void {
        if (!this.dataTable) {
            return;
        }

        const filters: { [key: string]: FilterMetadata | FilterMetadata[] } = {
            ...this.dataTable.filters
        };

        filters[column.field] = [{
            value,
            matchMode: this.filterMatchMode(column)
        }];
        this.dataTable.filters = filters;
        this.resetPaginator();
        this.loadData({
            first: 0,
            rows: this.rowsPerPage,
            filters
        });
    }

    renderCell(row: any, column: DataTableColumn): any {
        return column.formatter ? column.formatter(row) : this.getNestedValue(row, column.field);
    }

    getEllipsisTooltip(row: any, column: DataTableColumn): any {
        if (!column.ellipsisTooltip) {
            return this.renderCell(row, column);
        }
        const custom = column.ellipsisTooltip(row);
        return custom != null ? custom : this.renderCell(row, column);
    }

    getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    }

    getActions(column: DataTableColumn, row: any): DataTableAction[] {
        if (!column.actions) {
            return [];
        }

        return column.actions
            .filter(action => action.showIf ? action.showIf(row) : true)
            .map(action => ({
                ...action,
                command: () => action.command(row)
            }));
    }

    openModal(column: DataTableColumn, row: any): void {
        let component: Type<any> | null = null;

        if (column.dynamicModalComponent) {
            component = column.dynamicModalComponent(row);
        } else if (column.modalComponent) {
            component = column.modalComponent;
        }

        if (!component) {
            return;
        }

        const data = column.modalData ? column.modalData(row) : { row };
        const header = column.modalTitle || column.header || 'Details';

        this.ref = this.dialogService.open(component, {
            header,
            width: '70vw',
            data,
            dismissableMask: true,
            closable: true,
            styleClass: 'p-fluid'
        });
    }

    normalizeFilters(event: any = {}): Record<string, any> {
        const params: Record<string, any> = {};
        const tableFilters = event.filters ?? this.dataTable?.filters;
        if (!tableFilters) {
            return params;
        }

        const set = (f: string, k: string, v: any) => params[`filter[${f}][${k}]`] = v;

        const pushArr = (f: string, k: string, v: any) => {
            const key = `filter[${f}][${k}]`;
            if (!params[key]) {
                params[key] = [];
            }
            params[key].push(v);
        };

        const formatDateValue = (v: any) => {
            if (v instanceof Date) {
                const year = v.getFullYear();
                const month = String(v.getMonth() + 1).padStart(2, '0');
                const day = String(v.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }
            return v;
        };

        const getColumnByField = (field: string) => this.columns.find(c => c.field === field);

        const getColumnType = (field: string): FilterType => getColumnByField(field)?.filterType || FilterType.TEXT;

        const getFilterField = (field: string): string => getColumnByField(field)?.filterField || field;

        for (const field of Object.keys(tableFilters)) {
            const filterArr = tableFilters[field];
            if (!Array.isArray(filterArr) || !filterArr.length) {
                continue;
            }

            const rawValue = filterArr[0].value;
            const type = getColumnType(field);

            if (
                (type === FilterType.TEXT && !rawValue) ||
                (type === FilterType.BOOLEAN &&
                    (rawValue === null || rawValue === undefined)) ||
                ((type === FilterType.NUMERIC || type === FilterType.DATE) &&
                    !this.hasRangeFilterValue(rawValue)) ||
                (type !== FilterType.NUMERIC &&
                    type !== FilterType.DATE &&
                    type !== FilterType.BOOLEAN &&
                    rawValue === null) ||
                (Array.isArray(rawValue) && !rawValue.length)
            ) {
                continue;
            }

            const apiField = getFilterField(field);

            switch (type) {
                case FilterType.TEXT:
                    set(apiField, 'type', type);
                    set(apiField, 'value', rawValue);
                    break;

                case FilterType.SELECT:
                    set(apiField, 'type', type);
                    set(apiField, 'value', rawValue);
                    break;

                case FilterType.MULTISELECT:
                case FilterType.AUTOCOMPLETE: {
                    set(apiField, 'type', type);
                    const values = Array.isArray(rawValue)
                        ? rawValue
                        : (rawValue != null ? [rawValue] : []);
                    values.forEach(v => pushArr(apiField, 'value', v));
                    break;
                }

                case FilterType.BOOLEAN:
                    set(apiField, 'type', type);
                    set(apiField, 'value', rawValue ? 1 : 0);
                    break;

                case FilterType.NUMERIC:
                case FilterType.DATE: {
                    const range = this.parseRangeFilterValue(rawValue, type, formatDateValue);
                    if (!range) {
                        break;
                    }
                    set(apiField, 'type', 'range');
                    if (range.from != null) {
                        set(apiField, 'from', range.from);
                    }
                    if (range.to != null) {
                        set(apiField, 'to', range.to);
                    }
                    break;
                }

                case FilterType.CUSTOM:
                    if (rawValue && typeof rawValue === 'object') {
                        Object.entries(rawValue)
                            .forEach(([k, v]) => set(apiField, k, v));
                    }
                    break;

                default:
                    set(apiField, 'value', rawValue);
            }
        }

        return params;
    }

    exportData(): void {
        const event: any = this.dataTable.createLazyLoadMetadata();
        const first = event.first ?? 0;
        const rows = event.rows ?? this.rowsPerPage;
        const filters = this.normalizeFilters(event);
        const sort = this.resolveSort(event);
        const columns = this.visibleColumns
            .filter((c) => c.header && c.type !== DataTableColumnType.ACTIONS)
            .map((c) => ({
                field: c.field,
                header: c.header
            }));
        const params: any = {
            page: first / rows + 1,
            per_page: rows,
            columns,
            ...sort,
            ...filters
        };

        if (this.search) {
            params.search = this.search;
        }

        if (!this.exportDataFn) {
            console.error('exportDataFn is not provided');
            return;
        }

        this.exportLoading = true;
        this.cdr.markForCheck();
        const exportBody = this.buildExportBody(params);
        this.exportDataFn(exportBody).subscribe({
            next: blob => {
                this.exportLoading = false;
                this.cdr.markForCheck();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = this.title ? `${this.title}.csv` : 'export.csv';
                a.click();
                window.URL.revokeObjectURL(url);
            },
            error: () => {
                this.exportLoading = false;
                this.cdr.markForCheck();
            }
        });
    }

    private static readonly listingParamKeys = new Set([
        'page',
        'per_page',
        'sort_field',
        'sort_order',
        'sort',
        'order',
        'group_by',
    ]);

    buildFilterContext(column?: DataTableColumn, includeSearch = true): Record<string, any> {
        const params: Record<string, any> = Object.fromEntries(
            Object.entries(this.lastAppliedFilterParams).filter(
                ([key]) => !DataTableComponent.listingParamKeys.has(key)
            )
        );

        if (includeSearch && this.lastAppliedSearch) {
            params['search'] = this.lastAppliedSearch;
        }

        if (!column) {
            return params;
        }

        const apiField = column.filterField || column.field;

        return Object.fromEntries(
            Object.entries(params).filter(([key]) => !key.startsWith(`filter[${apiField}]`))
        );
    }

    /** Column filters only — autocomplete must not inherit global search or pagination. */
    buildAutocompleteContext(column: DataTableColumn): Record<string, any> {
        return this.buildFilterContext(column, false);
    }

    loadOptionsIfNeeded(column: DataTableColumn): void {
        if (!column.filterOptionsFn) {
            return;
        }

        if (this.optionsLoading.has(column.field)) {
            return;
        }

        this.optionsLoading.add(column.field);
        const context = this.buildFilterContext(column);
        column.filterOptionsFn(context)?.subscribe({
            next: options => {
                column.filterOptions = options;
                this.optionsLoading.delete(column.field);
                this.cdr.markForCheck();
            },
            error: () => {
                this.optionsLoading.delete(column.field);
            }
        });
    }

    resolveSort(event: any): { sort_field?: string; sort_order?: 'asc' | 'desc' } {
        if (event.sortField) {
            return {
                sort_field: event.sortField,
                sort_order: event.sortOrder === 1 ? 'asc' : 'desc'
            };
        }

        if (this.initialLoad && this.defaultSortField) {
            return {
                sort_field: this.defaultSortField,
                sort_order: this.defaultSortOrder
            };
        }

        return {};
    }

    buildExportBody(params: Record<string, any>): DataTableExportParams {
        const body: DataTableExportParams = {};
        const filter: Record<string, any> = {};

        for (const [key, value] of Object.entries(params)) {
            const match = key.match(/^filter\[(.+?)\]\[(.+?)\]$/);

            if (!match) {
                (body as any)[key] = value;
                continue;
            }

            const [, field, prop] = match;

            if (!filter[field]) {
                filter[field] = {};
            }

            filter[field][prop] = value;
        }

        if (Object.keys(filter).length) {
            body.filter = filter;
        }

        return body;
    }

    closeAllFilterOverlays(): void {
        const overlays = Array.from(
            document.querySelectorAll('.p-datatable-filter-overlay')
        );
        overlays.slice(0, -1).forEach(el => el.remove());
        document.querySelectorAll(
            '.p-select-overlay, .p-multiselect-overlay, .p-autocomplete-overlay, .p-autocomplete-panel, .p-datepicker-panel'
        ).forEach(el => el.remove());
    }

    onFilterOpen(column: DataTableColumn): void {
        setTimeout(() => {
            this.closeAllFilterOverlays();
        });

        this.loadOptionsIfNeeded(column);
    }

    getRangeFilterPart(
        filterConstraint: FilterMetadata,
        part: 'from' | 'to'
    ): string | number | Date | null {
        const value = filterConstraint.value;
        if (!value || typeof value !== 'object') {
            return null;
        }
        const partValue = value[part];
        return partValue === undefined || partValue === null || partValue === ''
            ? null
            : partValue;
    }

    getRangeFilterDatePart(
        filterConstraint: FilterMetadata,
        part: 'from' | 'to'
    ): Date | null {
        return this.coerceFilterDate(this.getRangeFilterPart(filterConstraint, part));
    }

    setRangeFilterDatePart(
        filterConstraint: FilterMetadata,
        part: 'from' | 'to',
        nextValue: Date | null
    ): void {
        this.setRangeFilterPart(filterConstraint, part, nextValue);
    }

    setRangeFilterPart(
        filterConstraint: FilterMetadata,
        part: 'from' | 'to',
        nextValue: string | number | Date | null
    ): void {
        const current = filterConstraint.value && typeof filterConstraint.value === 'object'
            ? { ...filterConstraint.value }
            : { from: null, to: null };
        current[part] = nextValue === '' ? null : nextValue;
        filterConstraint.value = this.hasRangeFilterValue(current) ? current : null;
    }

    private hasRangeFilterValue(value: any): boolean {
        if (!value || typeof value !== 'object') {
            return false;
        }
        const has = (part: 'from' | 'to') => {
            const v = value[part];
            if (v instanceof Date) {
                return !Number.isNaN(v.getTime());
            }
            return v !== null && v !== undefined && v !== '';
        };
        return has('from') || has('to');
    }

    private coerceFilterDate(value: string | number | Date | null): Date | null {
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }
        if (value === null || value === undefined || value === '') {
            return null;
        }
        if (typeof value !== 'string') {
            return null;
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [year, month, day] = value.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            return Number.isNaN(date.getTime()) ? null : date;
        }
        const usMatch = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (usMatch) {
            const date = new Date(+usMatch[3], +usMatch[1] - 1, +usMatch[2]);
            return Number.isNaN(date.getTime()) ? null : date;
        }
        return null;
    }

    private parseRangeFilterValue(
        rawValue: any,
        type: FilterType,
        formatDateValue: (v: any) => any
    ): { from: any; to: any } | null {
        if (!this.hasRangeFilterValue(rawValue)) {
            return null;
        }

        let from = rawValue.from ?? null;
        let to = rawValue.to ?? null;

        if (type === FilterType.DATE) {
            from = from !== null && from !== '' ? formatDateValue(from) : null;
            to = to !== null && to !== '' ? formatDateValue(to) : null;
        } else {
            from = from !== null && from !== '' ? Number(from) : null;
            to = to !== null && to !== '' ? Number(to) : null;
            if (from !== null && Number.isNaN(from)) {
                from = null;
            }
            if (to !== null && Number.isNaN(to)) {
                to = null;
            }
        }

        if (from === null && to === null) {
            return null;
        }

        return { from, to };
    }

    onAutocomplete(event: { query: string }, column: DataTableColumn) {
        if (!column.autocompleteCompleteMethod) {
            return;
        }

        const context = this.buildAutocompleteContext(column);
        column.autocompleteCompleteMethod(event.query, context).subscribe(options => {
            column.filterOptions = options;
            this.cdr.markForCheck();
        });
    }

    onEditableAutocomplete(event: { query: string }, column: DataTableColumn) {
        if (!column.editAutocompleteMethod) {
            return;
        }

        column.editAutocompleteMethod(event.query).subscribe(options => {
            column.editAutocompleteOptions = options;
            this.cdr.markForCheck();
        });
    }

    closeAllColumnFilters(): void {
        this.columnFilters?.forEach(cf => {
            if ((cf as any).overlayVisible) {
                cf.hide();
            }
        });
    }

    startEdit(row: object, column: DataTableColumn): void {
        this.editingCell = {
            row,
            field: column.field,
            value: structuredClone(this.getNestedValue(row, column.field))
        };
    }

    saveEdit(column: DataTableColumn): void {
        if (!this.editingCell) {
            return;
        }

        const { row, value } = this.editingCell;
        let finalValue = value;
        if (column.editType === FilterType.AUTOCOMPLETE || column.editType === FilterType.SELECT) {
            if (value && typeof value === 'object' && 'value' in value) {
                finalValue = value.value;
            }
        }
        row[column.field] = finalValue;
        column.onEditSave?.(finalValue, row);
        this.editingCell = null;
    }

    cancelEdit(): void {
        this.editingCell = null;
    }

    isEditing(row: any, column: DataTableColumn): boolean {
        return (this.editingCell?.row === row && this.editingCell?.field === column.field);
    }

    protected readonly FilterType = FilterType;

    protected readonly autocompletePanelClass = 'data-table-autocomplete-panel';

    protected readonly autocompletePanelStyle: Record<string, string> = {
        width: '280px',
        maxWidth: '280px',
        overflow: 'hidden'
    };

    protected readonly autocompleteScrollHeight = '200px';
    protected readonly DataTableColumnType = DataTableColumnType;
    protected readonly dateFilterFormat = 'mm/dd/yy';
    protected readonly dateFilterPlaceholder = 'mm/dd/yyyy';
    protected readonly triStateBooleanFilterOptions = TRI_STATE_BOOLEAN_FILTER_OPTIONS;
}
